import { Link } from "react-router-dom";
import { FileText, Search, Users, Shield, Send, CheckCircle, Clock, FileCheck } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const STEPS = [
  {
    number: 1,
    title: "Du meldest",
    description: "Du füllst das Formular aus und beschreibst, was passiert ist. Du kannst Bilder oder Screenshots hochladen.",
    icon: FileText,
    color: "#A0E8AF",
    duration: "5-10 Minuten"
  },
  {
    number: 2,
    title: "Wir prüfen",
    description: "Unser Team liest deine Meldung sorgfältig durch. Wir schauen uns alle Details und Beweise an.",
    icon: Search,
    color: "#BDE0FE",
    duration: "1-2 Tage"
  },
  {
    number: 3,
    title: "Wir bewerten",
    description: "Wir entscheiden, wie ernst der Fall ist und welche Hilfe du brauchst. Bei Fragen melden wir uns bei dir (falls du Kontaktdaten angegeben hast).",
    icon: Users,
    color: "#FFC4D6",
    duration: "1-3 Tage"
  },
  {
    number: 4,
    title: "Wir handeln",
    description: "Je nach Fall: Wir geben dir Tipps, vermitteln Hilfe, oder leiten bei schweren Fällen Informationen an die Polizei weiter.",
    icon: Shield,
    color: "#F6E05E",
    duration: "Variiert"
  },
  {
    number: 5,
    title: "Wir informieren dich",
    description: "Wenn du Kontaktdaten hinterlassen hast, halten wir dich auf dem Laufenden und sind für dich da.",
    icon: Send,
    color: "#A0E8AF",
    duration: "Laufend"
  },
];

export default function WasPassiert() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO title="Was passiert mit meiner Meldung?" description="So laeuft der Meldeprozess ab: Von der Einreichung bis zur Bearbeitung. Transparent, sicher und anonym. Erfahre, was wir mit deiner Meldung machen." path="/was-passiert" />
      
      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#A0E8AF]/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileCheck className="w-10 h-10 text-[#1A4731]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Was passiert mit deiner Meldung?
            </h1>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              Du fragst dich, was passiert, nachdem du auf "Absenden" geklickt hast? 
              Hier erklären wir dir Schritt für Schritt, wie es weitergeht.
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Connecting Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200 hidden md:block" />
            
            <div className="space-y-8">
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="relative flex gap-6">
                    {/* Number Circle */}
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 z-10 shadow-md"
                      style={{ backgroundColor: step.color }}
                    >
                      <span className="text-2xl font-bold text-white">{step.number}</span>
                    </div>
                    
                    {/* Content Card */}
                    <div className="flex-1 bg-white rounded-2xl p-6 shadow-soft border border-gray-100">
                      <div className="flex items-start gap-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 hidden sm:flex"
                          style={{ backgroundColor: `${step.color}30` }}
                        >
                          <Icon className="w-6 h-6" style={{ color: step.color }} />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-[#2D3748] mb-2" style={{ fontFamily: 'Nunito' }}>
                            {step.title}
                          </h3>
                          <p className="text-[#718096] mb-3">{step.description}</p>
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-[#A0AEC0]" />
                            <span className="text-[#A0AEC0]">{step.duration}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Important Notes */}
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="bg-[#A0E8AF]/10 rounded-2xl p-6">
              <CheckCircle className="w-8 h-8 text-[#1A4731] mb-3" />
              <h3 className="font-bold text-[#1A4731] mb-2">Du bleibst geschützt</h3>
              <p className="text-[#718096] text-sm">
                Deine Daten werden vertraulich behandelt. Wir geben nichts ohne deine Zustimmung weiter 
                (außer bei akuter Gefahr für dich oder andere).
              </p>
            </div>
            
            <div className="bg-[#BDE0FE]/10 rounded-2xl p-6">
              <Users className="w-8 h-8 text-[#1A365D] mb-3" />
              <h3 className="font-bold text-[#1A365D] mb-2">Echte Menschen</h3>
              <p className="text-[#718096] text-sm">
                Jede Meldung wird von echten Menschen gelesen - nicht von einem Computer. 
                Wir nehmen dich ernst.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <h3 className="text-xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Bereit, es zu versuchen?
            </h3>
            <Link 
              to="/melden"
              className="inline-flex items-center justify-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-8 py-4 rounded-full hover:bg-[#86D696] transition-all text-lg"
            >
              Jetzt melden
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
